import { type FieldValues, useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import {
  DropdownSingleSelect,
  type DropdownSingleSelectProps,
} from '~/chords/DropdownSingleSelect/DropdownSingleSelect';
import { FormField, type FormFieldProps } from '~/chords/FormField';

type FormFieldDropdownSingleSelectProps<
  TOptionProps,
  TFormFields extends FieldValues,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<
    DropdownSingleSelectProps<TOptionProps>,
    'name' | 'className' | 'value' | 'onValueChange'
  > & {
    // Use bivarianceHack to avoid having to have all clients of the component (outside WMG1)
    // to have to change the method signature of the callbacks they pass in.
    onValueChange?: {
      bivarianceHack(value: string | number): void;
    }['bivarianceHack'];
    /**
     * When true, converts the selected value to a number before setting it on the form.
     * Useful when dropdown option values are numeric strings and you want the form value to be a number.
     */
    valueAsNumber?: boolean;
  };

export function FormFieldDropdownSingleSelect<
  TOptionProps = unknown,
  TFormFields extends FieldValues = FieldValues,
>({
  name,
  id,
  label,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  labelAddon,
  fieldMessageClassName,
  onValueChange,
  showErrorMessage = true,
  valueAsNumber = false,
  ...dropdownProps
}: ComponentProps<
  FormFieldDropdownSingleSelectProps<TOptionProps, TFormFields>
>) {
  const {
    field: { ref: _ref, value: fieldValue, onBlur, onChange, ...fieldRest },
    fieldState: { invalid },
  } = useController({ name, disabled });

  // Normalize null/undefined to '' so the dropdown stays in a valid controlled
  // state (Radix Select's value is typed `string | undefined`). The form value
  // can still be null — e.g. after clearing a `valueAsNumber` field — without
  // leaking that null into the Select.
  const stringFieldValue =
    valueAsNumber && fieldValue != null && fieldValue !== ''
      ? String(fieldValue)
      : (fieldValue ?? '');

  return (
    <FormField
      name={name}
      id={id}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      fieldMessageClassName={fieldMessageClassName}
      showErrorMessage={showErrorMessage}
    >
      <DropdownSingleSelect
        id={id || name}
        {...fieldRest}
        name={name}
        value={stringFieldValue}
        onValueChange={(value) => {
          let resolvedValue: string | number = value;

          if (valueAsNumber) {
            const numValue = Number(value);

            resolvedValue = Number.isNaN(numValue) ? '' : numValue;
          }

          onChange(resolvedValue);
          onBlur();
          onValueChange?.(resolvedValue);
        }}
        disabled={disabled || readOnly}
        state={invalid ? 'error' : 'default'}
        {...dropdownProps}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onBlur();
          }

          dropdownProps.onOpenChange?.(isOpen);
        }}
        clearOptions={{
          ...(dropdownProps.clearOptions ?? {}),
          onClear: () => {
            onChange(valueAsNumber ? null : '');
            onBlur();
            dropdownProps.clearOptions?.onClear?.();
          },
        }}
      />
    </FormField>
  );
}

FormFieldDropdownSingleSelect.displayName = 'FormFieldDropdownSingleSelect';
